import { useDispatch, useSelector } from 'react-redux'
import { ActiveClaimCheckStatus } from '@typesApp/ClaimRecord'
import { AuthToken, AuthTokenArgs, CustomerContext } from '@typesApp/Session'
import { HostSession } from '@typesApp/Environment'
import { hasLocalAuthToken } from '@utils/authToken'
import { Mutex } from 'async-mutex'
import {
  activeClaimCheckStatusSelector,
  setActiveClaimRecord,
  setSkipActiveDataCheck,
  skipActiveDataCheckSelector,
} from '@redux/slices/claimStatusSlice'
import {
  authTokenError,
  authTokenReady,
  connectorActionModuleSelector,
  connectorInitGuestSession,
} from '@redux/slices/connectorSlice'
import { useLazyGetCustomerContextQuery, useLazyGetSessionQuery } from '@redux/queries/sessions'
import { useLazyGetActiveClaimRecordQuery } from '@redux/queries/claimRecords'
import { useGetAuthTokenMutation } from '@redux/queries/auth'
import { config } from '@config/config'

const PREFER_CACHE_VALUE = true

type GuestSessionResult = {
  sessionId: string
}

type GuestLoginParams = {
  userId: string
  cartId?: string
  sessionId?: string
}

type UseSessionReturn = {
  /**
   * Fetch customer context data (with session id)
   */
  initContext: () => Promise<CustomerContext>

  /**
   * Fetch customer session and auth token if needed
   */
  initSession: (session: HostSession) => Promise<void>

  /**
   * Trigger guest login then calls initContext and initSession
   */
  initGuestSession: () => Promise<GuestSessionResult>
}

const mutex = new Mutex()

/**
 * Initialize the session by fetching the commerce customer session and auth token if needed
 */
export const useSession = (): UseSessionReturn => {
  const dispatch = useDispatch()
  const [fetchContext] = useLazyGetCustomerContextQuery()
  const [fetchSession] = useLazyGetSessionQuery()
  const [fetchAuthToken] = useGetAuthTokenMutation()
  const [fetchActiveClaimRecord] = useLazyGetActiveClaimRecordQuery()
  const skipActiveDataCheck = useSelector(skipActiveDataCheckSelector)
  const activeClaimCheckStatus = useSelector(activeClaimCheckStatusSelector)
  const actionModule = useSelector(connectorActionModuleSelector)

  const initContext = async (): Promise<CustomerContext> => {
    try {
      return fetchContext().unwrap()
    } catch (error) {
      console.warn('directbilling session - get customer context API error', error)
      return { sessionId: '', isPartiallyAuthenticated: false }
    }
  }

  const initSession = async (hostSession: HostSession, skip?: boolean): Promise<void> => {
    const { sessionId } = hostSession
    const disableCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck
    if (!disableCheck && !sessionId) {
      console.warn('directbilling session - got empty sessionId, cannot fetch token')
      return
    }

    if (disableCheck && !hostSession?.userId) {
      console.warn('directbilling session - customerNumber required when sessionId not provided, cannot fetch token')
      return
    }

    if (hasLocalAuthToken(sessionId, hostSession.userId)) {
      dispatch(authTokenReady())

      // skip active data check for guest login or already in progress or already completed
      const shouldCheckActiveData =
        !skip &&
        !skipActiveDataCheck &&
        activeClaimCheckStatus !== ActiveClaimCheckStatus.PENDING &&
        activeClaimCheckStatus !== ActiveClaimCheckStatus.COMPLETE
      if (shouldCheckActiveData) {
        initCustomerActiveData(sessionId)
      }
      return
    }

    await mutex.waitForUnlock()

    // fetch auth token
    if (!mutex.isLocked()) {
      // check again if token already initialized
      if (hasLocalAuthToken(sessionId, hostSession?.userId)) return

      const releaseMutex = await mutex.acquire()
      try {
        const tokenResult = await initToken(sessionId)
        tokenResult && dispatch(setActiveClaimRecord(tokenResult?.claimRecordId))
      } catch (error) {
        console.warn('directbilling session - error fetching auth token', error)
        dispatch(authTokenError())
      } finally {
        releaseMutex()
      }
    }
  }

  const initCustomerActiveData = async (sessionId: string) => {
    const sessionData = await fetchSession(sessionId, PREFER_CACHE_VALUE).unwrap()
    if (!sessionData?.customer?.id) {
      console.warn('directbilling session - fetched customer but customerNumber is empty')
      return
    }

    const activeData = await fetchActiveClaimRecord({
      cartId: sessionData.cartId,
      customerNumber: sessionData.customer.id,
    }).unwrap()

    dispatch(setActiveClaimRecord(activeData?.id))
  }

  const initToken = async (sessionId: string): Promise<AuthToken | void> => {
    const sessionData = await fetchSession(sessionId, PREFER_CACHE_VALUE).unwrap()
    if (!sessionData?.customer?.id) {
      console.warn('directbilling session - fetched customer but customerNumber is empty')
      return
    }

    const args: AuthTokenArgs = {
      customerNumber: sessionData.customer.id,
      customerEmail: sessionData.customer?.email ?? '',
      sessionId,
    }

    // remove sessionId from payload if is optional
    if (!sessionId || !sessionId?.trim()?.length) {
      delete args.sessionId
    }

    return fetchAuthToken(args).unwrap()
  }

  const initGuestSession = async (): Promise<GuestSessionResult> => {
    if (typeof actionModule?.performGuestLogin !== 'function') {
      throw new Error('Bad configuration, missing actionModule.performGuestLogin action')
    }

    // skip active data check on guest login
    dispatch(setSkipActiveDataCheck(true))

    const loginResult = await actionModule.performGuestLogin()

    // commerce F/E may not include the guest sessionId or cartId
    if (isValidSessionId(loginResult.sessionId)) {
      return completeGuestLogin({
        userId: loginResult.id,
        cartId: loginResult.cartId,
        sessionId: loginResult.sessionId,
      })
    } else {
      const context = await initContext()
      return completeGuestLogin({ userId: loginResult.id, cartId: loginResult.cartId, sessionId: context.sessionId })
    }
  }

  const isValidSessionId = (sessionId?: string): boolean => {
    const disableCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck
    if (disableCheck) return true

    if (!sessionId) return false
    if (typeof sessionId === 'string') return sessionId.trim().length > 0
    const stringValue = `${sessionId}`
    return stringValue.trim().length > 0
  }

  const completeGuestLogin = async (params: GuestLoginParams): Promise<GuestSessionResult> => {
    const { userId, cartId, sessionId = '' } = params

    dispatch(connectorInitGuestSession({ userId, cartId, sessionId }))

    const hostSession: HostSession = {
      isGuest: true,
      isLoggedIn: false,
      userId,
      cartId,
      sessionId,
      isPartiallyAuthenticated: false,
    }

    await initSession(hostSession, true)
    return { sessionId: hostSession.sessionId }
  }

  return {
    initContext,
    initSession,
    initGuestSession,
  }
}
